import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileText, Calendar, DollarSign, User, Building, CreditCard, Printer, CheckCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { formatDate, formatCurrency } from '@/utils/dateFormat';
import { useCurrency } from '@/contexts/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/vtgmvoj5_Logo%201.jpg";
const COMPANY_NAME = "Wilyrash Farm";

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const PaySlip = () => {
  const { currencySymbol } = useCurrency();
  const [payslips, setPayslips] = useState([]);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const payslipPrintRef = useRef(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user?.role?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';
  const isManager = userRole === 'manager';
  const canViewPendingPayslips = isAdmin || isManager;
  const userId = user?.id;

  // Print payslip function
  const handlePrintPayslip = () => {
    const printContent = payslipPrintRef.current;
    if (!printContent || !selectedPayslip) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Pay Slip - ${selectedPayslip.staff_name || 'Employee'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #333; }
            .payslip-container { max-width: 800px; margin: 0 auto; }
            .company-header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #10b981; }
            .company-logo { width: 80px; height: 80px; border-radius: 50%; object-fit: cover; margin-bottom: 10px; }
            .company-name { font-size: 24px; font-weight: bold; color: #10b981; }
            .company-tagline { font-size: 12px; color: #666; }
            .payslip-title { font-size: 18px; font-weight: bold; margin-top: 15px; color: #333; }
            .employee-info { background: #f0fdf4; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .employee-info h3 { margin-bottom: 10px; color: #10b981; }
            .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
            .info-item label { font-size: 11px; color: #666; display: block; }
            .info-item span { font-weight: 600; }
            .section { margin-bottom: 20px; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; }
            .section h4 { margin-bottom: 10px; padding-bottom: 5px; border-bottom: 1px solid #e2e8f0; }
            .section.additions { background: #f0fdf4; border-color: #10b981; }
            .section.additions h4 { color: #10b981; }
            .section.deductions { background: #fef2f2; border-color: #ef4444; }
            .section.deductions h4 { color: #ef4444; }
            .row { display: flex; justify-content: space-between; padding: 5px 0; }
            .row.total { border-top: 1px solid #ccc; margin-top: 10px; padding-top: 10px; font-weight: bold; }
            .amount.positive { color: #10b981; }
            .amount.negative { color: #ef4444; }
            .net-pay { background: linear-gradient(to right, #f0fdf4, white); padding: 20px; border-radius: 8px; text-align: right; margin: 20px 0; }
            .net-pay-label { font-size: 14px; color: #666; }
            .net-pay-amount { font-size: 28px; font-weight: bold; color: #10b981; }
            .ytd-section { background: #eff6ff; padding: 15px; border-radius: 8px; }
            .ytd-section h4 { color: #3b82f6; margin-bottom: 10px; }
            .ytd-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; }
            .ytd-item label { font-size: 11px; color: #666; display: block; }
            .ytd-item span { font-size: 18px; font-weight: bold; color: #3b82f6; }
            .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #666; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="payslip-container">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  useEffect(() => {
    if (userId) {
      fetchPayslips();
      fetchEmployeeProfile();
    }
  }, [userId]);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/payroll/staff/${userId}`, getAuthHeader());
      setPayslips(response.data);
      if (response.data.length > 0) {
        // Fetch the first payslip individually to get dynamically calculated YTD values
        try {
          const firstPayslipResponse = await axios.get(`${API}/payroll/${response.data[0].id}`, getAuthHeader());
          setSelectedPayslip(firstPayslipResponse.data);
        } catch (error) {
          setSelectedPayslip(response.data[0]);
        }
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to fetch pay slips');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeProfile = async () => {
    try {
      const response = await axios.get(`${API}/employee-profiles/${userId}`, getAuthHeader());
      setEmployeeProfile(response.data);
    } catch (error) {
      console.log('No employee profile found');
    }
  };

  const getMonthName = (month) => MONTHS.find(m => m.value === month)?.label || '';

  // Mark payslip as viewed by employee (not admin)
  const markAsViewed = async (payslipId) => {
    if (isAdmin) return; // Admin viewing doesn't count
    try {
      await axios.put(`${API}/payroll/${payslipId}/mark-viewed`, {}, getAuthHeader());
    } catch (error) {
      console.log('Could not mark payslip as viewed');
    }
  };

  const handlePayslipSelect = async (payslipId) => {
    // Fetch the individual payroll record to get dynamically calculated YTD values
    try {
      const response = await axios.get(`${API}/payroll/${payslipId}`, getAuthHeader());
      setSelectedPayslip(response.data);
    } catch (error) {
      // Fallback to the list data if fetch fails
      const payslip = payslips.find(p => p.id === payslipId);
      setSelectedPayslip(payslip);
    }
    // Mark as viewed when employee selects a payslip
    markAsViewed(payslipId);
  };

  // Auto-mark first payslip as viewed when loaded (only if user can view it)
  useEffect(() => {
    const canView = canViewPendingPayslips || selectedPayslip?.payment_status?.toLowerCase() === 'paid';
    if (selectedPayslip && !isAdmin && canView) {
      markAsViewed(selectedPayslip.id);
    }
  }, [selectedPayslip?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payslip-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pay Slip
          </h1>
          <p className="text-lg text-slate-600 mt-2">View your salary details</p>
        </div>
      </div>

      {payslips.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Pay Slips Available</h3>
          <p className="text-slate-500">
            {employeeProfile 
              ? "Your pay slips will appear here once they are processed."
              : "Your employee profile has not been set up yet. Please contact your administrator."
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Pay Slip Selector */}
          <div className="lg:col-span-1">
            <div className="card p-4">
              <Label className="mb-2 block">Select Pay Period</Label>
              <Select value={selectedPayslip?.id} onValueChange={handlePayslipSelect}>
                <SelectTrigger data-testid="payslip-select">
                  <SelectValue placeholder="Select a pay slip" />
                </SelectTrigger>
                <SelectContent>
                  {payslips.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {getMonthName(p.month)} {p.year} - Period {p.period || 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee Info Card */}
            {employeeProfile && (
              <div className="card p-4 mt-4 space-y-3">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Employee Information
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-slate-500">Name:</span> <span className="font-medium">{user?.name}</span></p>
                  <p><span className="text-slate-500">Employee ID:</span> <span className="font-mono">{employeeProfile.employee_id}</span></p>
                  <p><span className="text-slate-500">Date of Hire:</span> {formatDate(employeeProfile.date_of_hire)}</p>
                </div>
              </div>
            )}

            {/* Bank Info Card */}
            {employeeProfile?.bank_name && (
              <div className="card p-4 mt-4 space-y-3">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Bank Details
                </h3>
                <div className="space-y-2 text-sm">
                  <p><span className="text-slate-500">Bank:</span> {employeeProfile.bank_name}</p>
                  {employeeProfile.bank_account_name && (
                    <p><span className="text-slate-500">Account Name:</span> <span className="font-medium">{employeeProfile.bank_account_name}</span></p>
                  )}
                  <p><span className="text-slate-500">Account Number:</span> <span className="font-mono">{employeeProfile.bank_account_number}</span></p>
                </div>
              </div>
            )}
          </div>

          {/* Pay Slip Details */}
          <div className="lg:col-span-2">
            {selectedPayslip && (
              <>
                {/* Check if user can view this payslip - staff can only view when status is 'paid' */}
                {!canViewPendingPayslips && selectedPayslip.payment_status?.toLowerCase() !== 'paid' ? (
                  /* Restricted view for non-admin/manager when payslip is pending */
                  <div className="card p-8 text-center">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">Pay Slip Pending</h3>
                    <p className="text-slate-500 mb-4">
                      Your pay slip for <strong>{getMonthName(selectedPayslip.month)} {selectedPayslip.year} - Period {selectedPayslip.period || 1}</strong> is currently being processed.
                    </p>
                    <span className="inline-block px-4 py-2 rounded-full bg-amber-100 text-amber-700 font-medium">
                      Status: PENDING
                    </span>
                    <p className="text-sm text-slate-400 mt-4">
                      You will be able to view the full details once the payroll has been completed and marked as paid.
                    </p>
                  </div>
                ) : (
                  /* Full payslip view for admin/manager OR when payment status is 'paid' */
              <div className="card" data-testid="payslip-details">
                {/* Print Button */}
                <div className="flex justify-end p-4 pb-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrintPayslip}
                    className="flex items-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Print PDF
                  </Button>
                </div>

                {/* Printable Content */}
                <div ref={payslipPrintRef}>
                  {/* Company Header */}
                  <div className="company-header text-center py-4 border-b-2 border-emerald-500">
                    <img 
                      src={COMPANY_LOGO} 
                      alt={COMPANY_NAME}
                      className="company-logo w-20 h-20 rounded-full object-cover mx-auto mb-2"
                    />
                    <h2 className="company-name text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {COMPANY_NAME}
                    </h2>
                    <p className="company-tagline text-xs text-slate-500">Poultry Farm Management</p>
                    <h3 className="payslip-title text-lg font-semibold mt-3">PAY SLIP</h3>
                  </div>

                  {/* Pay Period Header */}
                  <div className="p-6 border-b bg-gradient-to-r from-emerald-50 to-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          Pay Period
                        </h2>
                        <p className="text-slate-600 mt-1">
                          {getMonthName(selectedPayslip.month)} {selectedPayslip.year} - Period {selectedPayslip.period || 1}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          selectedPayslip.payment_status === 'paid'
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {selectedPayslip.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                        </span>
                        {selectedPayslip.payment_date && (
                          <p className="text-sm text-slate-500 mt-2">
                            Payment Date: {formatDate(selectedPayslip.payment_date)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Employee Details */}
                  <div className="employee-info p-6 border-b bg-slate-50">
                    <h3 className="font-semibold text-emerald-700 mb-2">Employee Information</h3>
                    <div className="info-grid grid grid-cols-2 gap-4 text-sm">
                      <div className="info-item">
                        <label className="text-slate-500">Employee Name</label>
                        <span className="font-semibold block">{selectedPayslip.staff_name}</span>
                      </div>
                      <div className="info-item">
                        <label className="text-slate-500">Employee ID</label>
                        <span className="font-mono font-semibold block">{selectedPayslip.employee_id}</span>
                      </div>
                    </div>
                  </div>

                {/* Earnings */}
                <div className="p-6 border-b">
                  <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                    Earnings
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Base Monthly Salary</span>
                      <span className="font-medium">{currencySymbol}{formatCurrency(selectedPayslip.base_salary)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Daily Rate ({currencySymbol}{formatCurrency(selectedPayslip.base_salary)} ÷ {selectedPayslip.days_in_month} days)</span>
                      <span className="font-medium">{currencySymbol}{formatCurrency(selectedPayslip.daily_rate)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Days Worked</span>
                      <span className="font-medium">{selectedPayslip.days_worked} of {selectedPayslip.days_in_month} days</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Entitled Day-Offs</span>
                      <span className="font-medium">{selectedPayslip.entitled_day_offs} days</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="font-semibold text-slate-700">Gross Pay ({currencySymbol}{formatCurrency(selectedPayslip.daily_rate)} × {selectedPayslip.days_worked})</span>
                      <span className="font-bold text-emerald-600">{currencySymbol}{formatCurrency(selectedPayslip.gross_pay)}</span>
                    </div>
                  </div>
                </div>

                {/* Additions */}
                <div className="p-6 border-b bg-emerald-50/30">
                  <h3 className="font-semibold text-emerald-700 mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Additions
                  </h3>
                  <div className="space-y-3">
                    {(selectedPayslip.data_allowance || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Data Allowance</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.data_allowance)}</span>
                      </div>
                    )}
                    {(selectedPayslip.medical_allowance || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Medical Allowance</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.medical_allowance)}</span>
                      </div>
                    )}
                    {(selectedPayslip.offsite_duty_allowance || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Off-Site Duty Allowance</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.offsite_duty_allowance)}</span>
                      </div>
                    )}
                    {/* Multiple Bonus Fields */}
                    {(selectedPayslip.bonus_1_amount || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Bonus: {selectedPayslip.bonus_1_description || 'Bonus 1'}</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.bonus_1_amount)}</span>
                      </div>
                    )}
                    {(selectedPayslip.bonus_2_amount || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Bonus: {selectedPayslip.bonus_2_description || 'Bonus 2'}</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.bonus_2_amount)}</span>
                      </div>
                    )}
                    {(selectedPayslip.bonus_3_amount || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Bonus: {selectedPayslip.bonus_3_description || 'Bonus 3'}</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.bonus_3_amount)}</span>
                      </div>
                    )}
                    {/* Legacy single bonus field for old records */}
                    {(selectedPayslip.bonus || 0) > 0 && !(selectedPayslip.bonus_1_amount || 0) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Bonus</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.bonus)}</span>
                      </div>
                    )}
                    {/* Legacy bonuses field for old records */}
                    {(selectedPayslip.bonuses || 0) > 0 && !(selectedPayslip.bonus || 0) && !(selectedPayslip.bonus_1_amount || 0) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Bonus</span>
                        <span className="font-medium text-emerald-600">+{currencySymbol}{formatCurrency(selectedPayslip.bonuses)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="font-semibold text-slate-700">Total Additions</span>
                      <span className="font-bold text-emerald-600">+{currencySymbol}{formatCurrency(
                        (selectedPayslip.total_additions || 0) > 0 
                          ? selectedPayslip.total_additions 
                          : (selectedPayslip.bonuses || 0)
                      )}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions */}
                <div className="p-6 border-b bg-red-50/30">
                  <h3 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Deductions
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Absent Days</span>
                      <span className="font-medium text-red-600">{selectedPayslip.absent_days} days</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Absence Deduction ({currencySymbol}{formatCurrency(selectedPayslip.daily_rate)} × {selectedPayslip.absent_days})</span>
                      <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(selectedPayslip.absence_deduction)}</span>
                    </div>
                    {(selectedPayslip.loan_repayment || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Loan Repayment</span>
                        <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(selectedPayslip.loan_repayment)}</span>
                      </div>
                    )}
                    {(selectedPayslip.damages || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Damages</span>
                        <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(selectedPayslip.damages)}</span>
                      </div>
                    )}
                    {(selectedPayslip.other_deduction_amount || 0) > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Other: {selectedPayslip.other_deduction_description || 'Unspecified'}</span>
                        <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(selectedPayslip.other_deduction_amount)}</span>
                      </div>
                    )}
                    {/* Legacy other_deductions for old records */}
                    {(selectedPayslip.other_deductions || 0) > 0 && !(selectedPayslip.other_deduction_amount || 0) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Other Deductions</span>
                        <span className="font-medium text-red-600">-{currencySymbol}{formatCurrency(selectedPayslip.other_deductions)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t">
                      <span className="font-semibold text-slate-700">Total Deductions</span>
                      <span className="font-bold text-red-600">-{currencySymbol}{formatCurrency(
                        (selectedPayslip.total_deductions || 0) > 0
                          ? selectedPayslip.total_deductions
                          : (selectedPayslip.absence_deduction || 0) + (selectedPayslip.other_deductions || 0)
                      )}</span>
                    </div>
                  </div>
                </div>

                {/* Net Pay */}
                <div className="p-6 bg-gradient-to-r from-emerald-50 to-white">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-slate-600">Net Pay</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Gross Pay + Additions - Deductions
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {currencySymbol}{formatCurrency(selectedPayslip.net_pay)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Staff Loan Section (if there is an active loan) */}
                {(selectedPayslip.loan_active_amount || 0) > 0 && (
                  <div className="p-6 bg-gradient-to-r from-amber-50 to-white border-t" data-testid="payslip-loan-section">
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="font-semibold text-amber-700">Staff Loan</h4>
                      {(selectedPayslip.loan_balance || 0) === 0 && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 inline-flex items-center gap-1" data-testid="loan-cleared-badge">
                          <CheckCircle className="w-3 h-3" /> Loan cleared
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-slate-600">Loan Amount</p>
                        <p className="text-lg font-semibold text-slate-800">
                          {currencySymbol}{formatCurrency(selectedPayslip.loan_active_amount || 0)}
                        </p>
                      </div>
                      {selectedPayslip.loan_active_date && (
                        <div>
                          <p className="text-sm text-slate-600">Date Disbursed</p>
                          <p className="text-lg font-semibold text-slate-800">{selectedPayslip.loan_active_date}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-slate-600">Repayment This Period</p>
                        <p className="text-lg font-semibold text-red-600">
                          {currencySymbol}{formatCurrency(selectedPayslip.loan_repayment || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Repaid to Date</p>
                        <p className="text-lg font-semibold text-emerald-600">
                          {currencySymbol}{formatCurrency(selectedPayslip.loan_total_repaid || 0)}
                        </p>
                        <p className="text-xs text-slate-500">{selectedPayslip.loan_payments_count || 0} payment{(selectedPayslip.loan_payments_count || 0) === 1 ? '' : 's'} made so far</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-3 border-t border-amber-200">
                      <span className="font-semibold text-slate-700">Balance Remaining to be Repaid</span>
                      <span className="font-bold text-amber-700 text-lg">
                        {currencySymbol}{formatCurrency(selectedPayslip.loan_balance || 0)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Medical Reimbursement Section (if any) */}
                {((selectedPayslip.med_reimburse_amount || 0) > 0 || (selectedPayslip.med_reimburse_mtd || 0) > 0 || (selectedPayslip.med_reimburse_ytd || 0) > 0) && (
                  <div className="p-6 bg-gradient-to-r from-purple-50 to-white border-t">
                    <h4 className="font-semibold text-purple-700 mb-3">Medical Reimbursement (For Record Only)</h4>
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-sm text-slate-600">Amount Paid This Period</p>
                        <p className="text-lg font-semibold text-purple-600">
                          {currencySymbol}{formatCurrency(selectedPayslip.med_reimburse_amount || 0)}
                        </p>
                      </div>
                      {selectedPayslip.med_reimburse_date && (
                        <div>
                          <p className="text-sm text-slate-600">Date Paid</p>
                          <p className="text-lg font-semibold text-purple-600">
                            {selectedPayslip.med_reimburse_date}
                          </p>
                        </div>
                      )}
                    </div>
                    {selectedPayslip.med_reimburse_reason && (
                      <div className="mb-3">
                        <p className="text-sm text-slate-600">Reason</p>
                        <p className="text-sm font-medium text-purple-700">{selectedPayslip.med_reimburse_reason}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-purple-200">
                      <div>
                        <p className="text-sm text-slate-600">Total This Month</p>
                        <p className="text-xl font-bold text-purple-600">
                          {currencySymbol}{formatCurrency(selectedPayslip.med_reimburse_mtd || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Year-to-Date</p>
                        <p className="text-xl font-bold text-purple-600">
                          {currencySymbol}{formatCurrency(selectedPayslip.med_reimburse_ytd || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Year-to-Date Totals */}
                <div className="p-6 bg-gradient-to-r from-blue-50 to-white border-t">
                  <h4 className="font-semibold text-blue-700 mb-3">Year-to-Date Totals ({selectedPayslip.year})</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">YTD Gross Pay</p>
                      <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {currencySymbol}{formatCurrency(selectedPayslip.ytd_gross_pay || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">YTD Net Pay</p>
                      <p className="text-2xl font-bold text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {currencySymbol}{formatCurrency(selectedPayslip.ytd_net_pay || 0)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {selectedPayslip.notes && (
                  <div className="p-4 bg-slate-50 border-t">
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Notes:</span> {selectedPayslip.notes}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <div className="p-4 border-t text-center text-xs text-slate-400">
                  Generated by Wilyrash Farm Payroll System
                </div>
              </div>
              </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaySlip;
